import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useCurrency } from '../context/CurrencyContext';
import { Layout } from '../components/Layout';
import { staffAPI, restaurantAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getRoleBadgeColor } from '../lib/utils';
import {
  UserPlus,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  Users,
  Mail,
  Key,
  MapPin,
  Building2,
  Star,
  Plus,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  CalendarX2,
  DollarSign,
  ChevronRight,
} from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';

const ADV_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const advHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function Staff() {
  const { user, canManage } = useAuth();
  const { selectedLocationId, isAllLocations } = useLocation();
  const [staff, setStaff] = useState([]);
  const [staffWithLocations, setStaffWithLocations] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [assigningStaff, setAssigningStaff] = useState(null);
  const [staffPin, setStaffPin] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'staff',
    pin: '',
    hourly_wage: '',
    unavailability: [],
  });

  const fmtDate = (d) => d
    ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  const addUnavailability = () => setFormData(prev => ({
    ...prev, unavailability: [...prev.unavailability, { start_date: '', end_date: '' }],
  }));
  const removeUnavailability = (idx) => setFormData(prev => ({
    ...prev, unavailability: prev.unavailability.filter((_, i) => i !== idx),
  }));
  const updateUnavailability = (idx, field, value) => setFormData(prev => ({
    ...prev,
    unavailability: prev.unavailability.map((r, i) => i === idx ? { ...r, [field]: value } : r),
  }));
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationForm, setLocationForm] = useState({
    location_id: '',
    role: 'staff',
    is_primary: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Cash Advances state
  const [showAdvances, setShowAdvances] = useState(false);
  const [advances, setAdvances] = useState([]);
  const [advLoading, setAdvLoading] = useState(false);
  const [advDialogOpen, setAdvDialogOpen] = useState(false);
  const [advForm, setAdvForm] = useState({ employee_id: '', amount: '' });
  const [deductState, setDeductState] = useState(null); // {id, amount}

  const { formatMoney } = useCurrency();

  // Build per-employee outstanding CA balance map
  const advanceBalanceMap = useMemo(() => {
    const map = {};
    advances.forEach(adv => {
      if ((adv.remaining_balance || 0) > 0) {
        map[adv.employee_id] = (map[adv.employee_id] || 0) + adv.remaining_balance;
      }
    });
    return map;
  }, [advances]);

  const isMultiLocation = restaurants.length > 1;
  const activeLocId = isAllLocations ? user?.restaurant_id : selectedLocationId;

  const fetchData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const [restaurantsRes] = await Promise.all([
        restaurantAPI.getAll(),
      ]);
      setRestaurants(restaurantsRes.data);

      if (isAllLocations) {
        const staffRes = await staffAPI.getWithLocations();
        setStaffWithLocations(staffRes.data);
        setStaff([]);
      } else if (activeLocId) {
        const staffRes = await staffAPI.getAll(activeLocId);
        setStaff(staffRes.data);
        setStaffWithLocations([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAdvances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedLocationId, isAllLocations]);

  const fetchAdvances = async () => {
    setAdvLoading(true);
    try {
      const { data } = await axios.get(`${ADV_BASE}/advances`, { headers: advHeaders() });
      setAdvances(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load advances');
    } finally {
      setAdvLoading(false);
    }
  };

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    if (!advForm.employee_id || !advForm.amount) return;
    try {
      await axios.post(`${ADV_BASE}/advances/create`, {
        employee_id: advForm.employee_id,
        amount: parseFloat(advForm.amount),
      }, { headers: advHeaders() });
      toast.success('Advance created');
      setAdvDialogOpen(false);
      setAdvForm({ employee_id: '', amount: '' });
      fetchAdvances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create advance');
    }
  };

  const handleMarkRepaid = async (advanceId) => {
    try {
      await axios.post(`${ADV_BASE}/advances/repaid`, { advance_id: advanceId }, { headers: advHeaders() });
      toast.success('Marked as repaid');
      fetchAdvances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeduct = async () => {
    if (!deductState?.id || !deductState?.amount) return;
    try {
      await axios.post(`${ADV_BASE}/advances/deduct`, {
        advance_id: deductState.id,
        amount: parseFloat(deductState.amount),
      }, { headers: advHeaders() });
      toast.success('Deduction recorded');
      setDeductState(null);
      fetchAdvances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deduct');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingStaff && selectedLocations.length === 0) {
      toast.error('Select at least one location');
      return;
    }
    setSubmitting(true);

    try {
      if (editingStaff) {
        await staffAPI.update(editingStaff.id, {
          name: formData.name,
          role: formData.role,
          pin: formData.pin || null,
          location_ids: selectedLocations,
          hourly_wage: formData.hourly_wage !== '' ? parseFloat(formData.hourly_wage) : null,
          unavailability: formData.unavailability.filter(r => r.start_date && r.end_date),
        });
        toast.success('Staff member updated!');
      } else {
        const targetRestaurantId = selectedLocations[0] || activeLocId || restaurants[0]?.id;
        if (!targetRestaurantId) {
          toast.error('No restaurant available');
          return;
        }
        await staffAPI.create(targetRestaurantId, {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          pin: formData.pin || null,
          location_ids: selectedLocations,
          hourly_wage: formData.hourly_wage !== '' ? parseFloat(formData.hourly_wage) : null,
          unavailability: formData.unavailability.filter(r => r.start_date && r.end_date),
        });
        toast.success('Staff member added!');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (staffMember) => {
    if (!confirm(`Remove ${staffMember.name} from staff?`)) return;

    try {
      await staffAPI.delete(staffMember.id);
      toast.success('Staff member removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff member');
    }
  };

  const handleEdit = async (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      email: staffMember.email,
      name: staffMember.name,
      role: staffMember.role || staffMember.primary_role,
      pin: '',
      hourly_wage: staffMember.hourly_wage != null ? String(staffMember.hourly_wage) : '',
      unavailability: staffMember.unavailability || [],
    });
    // Pre-select current locations
    const currentLocs = (staffMember.locations || []).map(l => l.location_id);
    setSelectedLocations(currentLocs);
    setStaffPin(null);
    setShowPin(false);
    setDialogOpen(true);
    
    // Fetch staff detail to get PIN if authorized
    try {
      setPinLoading(true);
      const response = await staffAPI.getOne(staffMember.id);
      if (response.data.pin) {
        setStaffPin(response.data.pin);
      }
    } catch (error) {
      console.log('PIN not available');
    } finally {
      setPinLoading(false);
    }
  };

  const openLocationDialog = (staffMember) => {
    setAssigningStaff(staffMember);
    setLocationForm({
      location_id: '',
      role: 'staff',
      is_primary: false,
    });
    setLocationDialogOpen(true);
  };

  const handleAssignLocation = async (e) => {
    e.preventDefault();
    if (!assigningStaff) return;
    setSubmitting(true);

    try {
      await staffAPI.assignLocation(assigningStaff.id, {
        staff_id: assigningStaff.id,
        location_id: locationForm.location_id,
        role: locationForm.role,
        is_primary: locationForm.is_primary,
      });
      toast.success('Location assigned!');
      setLocationDialogOpen(false);
      setAssigningStaff(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveLocation = async (staffId, locationId, locationName) => {
    if (!confirm(`Remove this staff from ${locationName}?`)) return;

    try {
      await staffAPI.removeLocation(staffId, locationId);
      toast.success('Location assignment removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove location');
    }
  };

  const handleSetPrimary = async (staffId, locationId) => {
    try {
      await staffAPI.updateLocation(staffId, locationId, { is_primary: true });
      toast.success('Primary location updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update primary location');
    }
  };

  const resetForm = () => {
    setEditingStaff(null);
    setFormData({ email: '', name: '', role: 'staff', pin: '', hourly_wage: '', unavailability: [] });
    setSelectedLocations([]);
    setStaffPin(null);
    setShowPin(false);
  };

  const handleResetPin = async () => {
    if (!editingStaff) return;
    
    try {
      setPinLoading(true);
      const response = await staffAPI.resetPin(editingStaff.id);
      setStaffPin(response.data.pin);
      setShowPin(true);
      toast.success('PIN reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleCopyPin = () => {
    if (staffPin) {
      navigator.clipboard.writeText(staffPin);
      toast.success('PIN copied to clipboard');
    }
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Get available locations for assignment (excluding already assigned)
  const getAvailableLocations = () => {
    if (!assigningStaff) return restaurants;
    const assignedIds = (assigningStaff.locations || []).map(l => l.location_id);
    return restaurants.filter(r => !assignedIds.includes(r.id));
  };

  // Combine staff lists for display
  const displayStaff = isAllLocations ? staffWithLocations : staff;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Staff Management
              {canManage && <HelpTooltip text="Add employees, assign roles, manage permissions, and configure PIN access for time clock usage." learnMoreUrl="/help/staff" />}
            </h1>
            <p className="text-slate-500 mt-1">
              Manage your team members{isMultiLocation ? ' and their location assignments' : ''}
            </p>
            {canManage && (
              <div className="flex gap-1 mt-3 bg-slate-100 rounded-lg p-1 w-fit">
                <button
                  onClick={() => setShowAdvances(false)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${!showAdvances ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="tab-staff-btn"
                >
                  Staff
                </button>
                <button
                  onClick={() => { setShowAdvances(true); fetchAdvances(); }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${showAdvances ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="tab-advances-btn"
                >
                  Cash Advances
                </button>
              </div>
            )}
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} data-testid="add-staff-btn">
                <UserPlus className="mr-2 h-4 w-4" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="flex flex-col max-h-[90vh]">
              <DialogHeader className="shrink-0">
                <DialogTitle>
                  {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
                </DialogTitle>
                <DialogDescription>
                  {editingStaff ? 'Update staff member details' : 'Add a new team member to your business'}
                </DialogDescription>
              </DialogHeader>
              {/* Scrollable form body — mobile scroll fix */}
              <div className="overflow-y-auto flex-1 min-h-0 pb-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!!editingStaff}
                    required
                    data-testid="staff-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="staff-name-input"
                  />
                </div>
                <div className="space-y-2">
367|                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger data-testid="staff-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                

                {/* Hourly Wage — owner/manager only */}
                <div className="space-y-2">
                  <Label htmlFor="hourly_wage" className="flex items-center gap-1.5">
                    Hourly Wage
                    <span className="text-xs text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                    <Input
                      id="hourly_wage"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="e.g. 18.50"
                      value={formData.hourly_wage}
                      onChange={(e) => setFormData(prev => ({ ...prev, hourly_wage: e.target.value }))}
                      className="pl-7"
                      data-testid="staff-wage-input"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Used for payroll cost estimates. Never visible to staff.</p>
                </div>

                {/* PIN Section - Different for edit vs create */}
                {editingStaff ? (
                  <div className="space-y-2">
                    <Label>Staff PIN</Label>
                    {pinLoading ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading PIN...
                      </div>
                    ) : staffPin ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
                          <Key className="h-4 w-4 text-slate-500" />
                          <span className="font-mono font-semibold text-lg tracking-wider" data-testid="staff-pin-display">
                            {showPin ? staffPin : '••••'}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPin(!showPin)}
                            className="ml-auto text-slate-500 hover:text-slate-700"
                          >
                            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleCopyPin}
                          title="Copy PIN"
                          data-testid="copy-pin-btn"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleResetPin}
                          title="Reset PIN"
                          disabled={pinLoading}
                          data-testid="reset-pin-btn"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">No PIN set</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleResetPin}
                          disabled={pinLoading}
                        >
                          <Key className="mr-2 h-4 w-4" />
                          Generate PIN
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-slate-500">
                      PIN is visible only to authorized personnel
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="pin">PIN (Optional)</Label>
                    <Input
                      id="pin"
                      type="text"
                      placeholder="4-digit PIN for quick login"
                      value={formData.pin}
                      onChange={(e) => setFormData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      maxLength={4}
                      data-testid="staff-pin-input"
                    />
                    <p className="text-xs text-slate-500">
                      Leave empty to auto-generate. Staff can use this PIN to quickly clock in/out
                    </p>
                  </div>
                )}

                {/* Unavailable Dates */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-1.5">
                      <CalendarX2 size={14} className="text-amber-500" />
                      Unavailable Dates
                    </Label>
                    <button
                      type="button"
                      onClick={addUnavailability}
                      className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
                      data-testid="add-unavailability-btn"
                    >
                      <Plus size={12} /> Add Range
                    </button>
                  </div>
                  {formData.unavailability.length === 0 ? (
                    <p className="text-xs text-slate-400 py-1">No unavailability set</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.unavailability.map((range, idx) => (
                        <div key={idx} className="flex items-center gap-2" data-testid={`unavail-row-${idx}`}>
                          <Input
                            type="date"
                            value={range.start_date}
                            onChange={e => updateUnavailability(idx, 'start_date', e.target.value)}
                            className="text-xs h-8"
                            data-testid={`unavail-start-${idx}`}
                          />
                          <span className="text-slate-400 text-xs shrink-0">to</span>
                          <Input
                            type="date"
                            value={range.end_date}
                            onChange={e => updateUnavailability(idx, 'end_date', e.target.value)}
                            className="text-xs h-8"
                            data-testid={`unavail-end-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeUnavailability(idx)}
                            className="text-red-400 hover:text-red-600 transition-colors shrink-0"
                            data-testid={`unavail-remove-${idx}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-400">Single day: set start = end date.</p>
                </div>

                {/* Multi-Location Assignment */}
                {restaurants.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <MapPin size={14} className="text-orange-500" />
                      Assign Locations {!editingStaff && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="border border-slate-200 rounded-lg max-h-[160px] overflow-y-auto">
                      {restaurants.map((restaurant) => {
                        const isChecked = selectedLocations.includes(restaurant.id);
                        return (
                          <label
                            key={restaurant.id}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0 ${isChecked ? 'bg-orange-50' : ''}`}
                            data-testid={`location-checkbox-${restaurant.id}`}
                          >
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                setSelectedLocations(prev =>
                                  checked
                                    ? [...prev, restaurant.id]
                                    : prev.filter(id => id !== restaurant.id)
                                );
                              }}
                            />
                            <Building2 size={14} className="text-slate-400" />
                            <span className="text-sm text-slate-700">{restaurant.location_name}</span>
                          </label>
                        );
                      })}
                    </div>
                    {!editingStaff && selectedLocations.length === 0 && (
                      <p className="text-xs text-red-500">Select at least one location</p>
                    )}
                    <p className="text-xs text-slate-400">
                      {selectedLocations.length} of {restaurants.length} selected
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={submitting}
                    data-testid="staff-submit-btn"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : editingStaff ? (
                      'Update'
                    ) : (
                      'Add Staff'
                    )}
                  </Button>
                </div>
              </form>
              </div>{/* end scrollable form body */}
            </DialogContent>
          </Dialog>
        </div>

        {/* Staff Stats */}
        {!showAdvances && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Users className="text-slate-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{displayStaff.length}</p>
                  <p className="text-sm text-slate-500">Total Staff</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {displayStaff.filter(s => (s.role || s.primary_role) === 'manager').length}
                  </p>
                  <p className="text-sm text-slate-500">Managers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {displayStaff.filter(s => (s.role || s.primary_role) === 'staff').length}
                  </p>
                  <p className="text-sm text-slate-500">Staff</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {isMultiLocation && (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Building2 className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{restaurants.length}</p>
                    <p className="text-sm text-slate-500">Locations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {!isMultiLocation && (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Key className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {displayStaff.filter(s => s.pin).length}
                    </p>
                    <p className="text-sm text-slate-500">With PIN</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Staff List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">All Staff Members</CardTitle>
          </CardHeader>
          <CardContent>
            {displayStaff.length === 0 ? (
              <div className="text-center py-10" data-testid="staff-empty-state">
                <Users className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-2 text-slate-500 font-medium">No staff members yet</p>
                <p className="text-sm text-slate-400 mt-1 mb-4">Start by adding your first staff member to get started</p>
                <Button onClick={openNewDialog} data-testid="staff-empty-cta">
                  <UserPlus className="mr-2 h-4 w-4" />
                  + Add Staff Member
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {displayStaff.map((member) => (
                  <div 
                    key={member.id} 
                    className="border border-slate-200 rounded-lg p-4"
                    data-testid={`staff-card-${member.id}`}
                  >
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                      <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-base sm:text-lg font-medium text-slate-600">
                            {member.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-medium text-slate-900 truncate">{member.name}</h3>
                            <Badge className={getRoleBadgeColor(member.role || member.primary_role)}>
                              {(member.role || member.primary_role)?.charAt(0).toUpperCase() + (member.role || member.primary_role)?.slice(1)}
                            </Badge>
                            {advanceBalanceMap[(member.id || member.user_id)] > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-full"
                                data-testid={`ca-balance-badge-${member.id || member.user_id}`}
                              >
                                CA: {formatMoney(advanceBalanceMap[member.id || member.user_id])}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1 truncate">
                            <Mail size={12} className="shrink-0" />
                            <span className="truncate">{member.email}</span>
                          </p>
                          {member.pin && (
                            <div className="flex items-center gap-2 mt-1.5" data-testid={`staff-pin-row-${member.id}`}>
                              <Key size={12} className="text-purple-500" />
                              <span className="text-sm font-mono font-semibold text-purple-700 tracking-wider">PIN: {member.pin}</span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(member.pin);
                                  toast.success('PIN copied');
                                }}
                                className="p-0.5 rounded hover:bg-purple-100 text-purple-400 hover:text-purple-600 transition-colors"
                                title="Copy PIN"
                                data-testid={`copy-list-pin-${member.id}`}
                              >
                                <Copy size={12} />
                              </button>
                            </div>
                          )}
                          
                          {/* Upcoming Unavailability */}
                          {(() => {
                            const today = new Date().toISOString().split('T')[0];
                            const upcoming = (member.unavailability || []).filter(r => r.end_date >= today);
                            if (!upcoming.length) return null;
                            return (
                              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`unavail-display-${member.id}`}>
                                {upcoming.map((r, i) => (
                                  <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                                    {r.start_date === r.end_date
                                      ? `Unavail: ${fmtDate(r.start_date)}`
                                      : `Unavail: ${fmtDate(r.start_date)} – ${fmtDate(r.end_date)}`}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}

                          {/* Location Assignments */}
                          {isMultiLocation && (
                            <div className="mt-3">
                              <p className="text-xs font-medium text-slate-500 mb-2">ASSIGNED LOCATIONS</p>
                              {member.locations && member.locations.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {member.locations.map((loc) => (
                                    <div 
                                      key={loc.id}
                                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                                        loc.is_primary 
                                          ? 'bg-orange-100 text-orange-800 border border-orange-200' 
                                          : 'bg-slate-100 text-slate-700'
                                      }`}
                                    >
                                      <MapPin size={12} />
                                      <span>{loc.location_name}</span>
                                      <span className="text-xs opacity-70">({loc.role})</span>
                                      {loc.is_primary && (
                                        <Star size={12} className="text-orange-500 fill-orange-500" />
                                      )}
                                      <button
                                        onClick={() => handleRemoveLocation(member.id, loc.location_id, loc.location_name)}
                                        className="ml-1 hover:text-red-600"
                                        title="Remove from location"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-400 italic">No locations assigned</p>
                              )}
                              
                              {/* Add Location Button */}
                              {getAvailableLocations().length > 0 || !member.locations?.length ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="mt-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 p-0 h-auto"
                                  onClick={() => openLocationDialog(member)}
                                  data-testid={`assign-location-${member.id}`}
                                >
                                  <Plus size={14} className="mr-1" />
                                  Assign to Location
                                </Button>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`staff-menu-${member.id}`}>
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(member)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit Details
                          </DropdownMenuItem>
                          {isMultiLocation && (
                            <DropdownMenuItem onClick={() => openLocationDialog(member)}>
                              <MapPin className="mr-2 h-4 w-4" />
                              Manage Locations
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete(member)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove Staff
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>)} {/* end !showAdvances */}

        {/* Cash Advances Section */}
        {showAdvances && (
          <Card className="border-slate-200" data-testid="cash-advances-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign size={20} className="text-green-600" />
                  Cash Advances
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => setAdvDialogOpen(true)}
                  data-testid="add-advance-btn"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Advance
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {advLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : advances.length === 0 ? (
                <div className="text-center py-10" data-testid="advances-empty">
                  <DollarSign className="mx-auto h-10 w-10 text-slate-300" />
                  <p className="mt-2 text-slate-500">No advances recorded yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Employee</th>
                        <th className="text-right py-2 px-3 font-medium text-slate-600">Amount</th>
                        <th className="text-right py-2 px-3 font-medium text-slate-600">Remaining</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advances.map((adv) => (
                        <tr key={adv.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`advance-row-${adv.id}`}>
                          <td className="py-3 px-3 font-medium text-slate-900">{adv.employee_name}</td>
                          <td className="py-3 px-3 text-right text-slate-700">${adv.amount.toFixed(2)}</td>
                          <td className="py-3 px-3 text-right font-semibold text-slate-900">${adv.remaining_balance.toFixed(2)}</td>
                          <td className="py-3 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${adv.status === 'repaid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                              {adv.status === 'repaid' ? 'Repaid' : 'Active'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-slate-500 text-xs">{new Date(adv.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-3">
                            {adv.status === 'active' && (
                              <div className="flex items-center gap-2">
                                {deductState?.id === adv.id ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      min="0.01"
                                      step="0.01"
                                      placeholder="Amount"
                                      value={deductState.amount}
                                      onChange={(e) => setDeductState(s => ({ ...s, amount: e.target.value }))}
                                      className="w-24 h-7 text-sm"
                                      data-testid={`deduct-input-${adv.id}`}
                                    />
                                    <Button size="sm" className="h-7 px-2 text-xs bg-orange-500 hover:bg-orange-600" onClick={handleDeduct} data-testid={`deduct-save-${adv.id}`}>
                                      Save
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setDeductState(null)}>
                                      ✕
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 px-2 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                                      onClick={() => setDeductState({ id: adv.id, amount: '' })}
                                      data-testid={`deduct-btn-${adv.id}`}
                                    >
                                      Deduct
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700"
                                      onClick={() => handleMarkRepaid(adv.id)}
                                      data-testid={`repaid-btn-${adv.id}`}
                                    >
                                      Mark Repaid
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add Advance Dialog */}
        <Dialog open={advDialogOpen} onOpenChange={setAdvDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add Cash Advance</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAdvance} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select value={advForm.employee_id} onValueChange={(v) => setAdvForm(s => ({ ...s, employee_id: v }))}>
                  <SelectTrigger data-testid="advance-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {displayStaff.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={advForm.amount}
                    onChange={(e) => setAdvForm(s => ({ ...s, amount: e.target.value }))}
                    className="pl-7"
                    required
                    data-testid="advance-amount-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setAdvDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="flex-1" disabled={!advForm.employee_id || !advForm.amount} data-testid="advance-submit-btn">
                  Create Advance
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign Location Dialog */}
        <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign to Location</DialogTitle>
              <DialogDescription>
                Assign {assigningStaff?.name} to a location
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAssignLocation} className="space-y-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Select
                  value={locationForm.location_id}
                  onValueChange={(value) => setLocationForm(prev => ({ ...prev, location_id: value }))}
                >
                  <SelectTrigger data-testid="location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableLocations().map((restaurant) => (
                      <SelectItem key={restaurant.id} value={restaurant.id}>
                        {restaurant.location_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Role at this Location</Label>
                <Select
                  value={locationForm.role}
                  onValueChange={(value) => setLocationForm(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Staff can have different roles at different locations
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_primary"
                  checked={locationForm.is_primary}
                  onCheckedChange={(checked) => setLocationForm(prev => ({ ...prev, is_primary: checked }))}
                />
                <Label htmlFor="is_primary" className="text-sm font-normal cursor-pointer">
                  Set as primary location
                </Label>
              </div>
              <p className="text-xs text-slate-500 -mt-2">
                Primary location is used for default scheduling and time clock
              </p>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setLocationDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={submitting || !locationForm.location_id}
                  data-testid="assign-location-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
